import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Text, Badge, Group, Stack, Button, Divider } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import type { Assessment } from '../../../store/impactStore';
import { useIncidentZones } from '../../../hooks/useIncidentZones';
import ZoneMapOverlay from '../../../components/map/ZoneMapOverlay';

// Fix default marker icons
delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const CATEGORY_COLORS: Record<string, string> = {
  affected: '#16a34a',
  minor: '#d97706',
  major: '#ea580c',
  destroyed: '#dc2626',
  inaccessible: '#7c3aed',
};

const SEVERITY_COLORS: Record<number, string> = {
  1: '#16a34a',
  2: '#22c55e',
  3: '#d97706',
  4: '#ea580c',
  5: '#dc2626',
};

function createColoredIcon(color: string) {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      width: 24px; height: 24px; border-radius: 50%;
      background: ${color}; border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -14],
  });
}

function FitBounds({ assessments }: { assessments: Assessment[] }) {
  const map = useMap();
  useEffect(() => {
    const points = assessments
      .filter(a => a.latitude && a.longitude)
      .map(a => [a.latitude!, a.longitude!] as [number, number]);
    if (points.length > 0) {
      map.fitBounds(points, { padding: [40, 40], maxZoom: 14 });
    }
  }, [assessments, map]);
  return null;
}

interface Props {
  assessments?: Assessment[];
  height?: number | string;
  onMarkerClick?: (a: Assessment) => void;
  colorBy?: 'category' | 'severity';
}

function IncidentZoneLayer() {
  const { activeZones } = useIncidentZones();
  return <ZoneMapOverlay zones={activeZones} interactive />;
}

export default function AssessmentMap({ assessments = [], height = 400, onMarkerClick, colorBy = 'category' }: Props) {
  const navigate = useNavigate();
  const geoAssessments = assessments.filter(a => a.latitude && a.longitude);

  const center: [number, number] = geoAssessments.length > 0
    ? [geoAssessments[0].latitude!, geoAssessments[0].longitude!]
    : [39.8283, -98.5795];

  const getColor = (assessment: Assessment) => {
    if (colorBy === 'severity') {
      return SEVERITY_COLORS[assessment.severity || 0] || '#6b7280';
    }
    return CATEGORY_COLORS[assessment.damageCategory] || '#3F72AF';
  };

  return (
    <MapContainer
      center={center}
      zoom={geoAssessments.length > 0 ? 12 : 4}
      style={{ height, width: '100%', borderRadius: 'var(--radius, 10px)' }}
    >
      <LayersControl position="topright">
        <LayersControl.BaseLayer checked name="Street">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Satellite">
          <TileLayer
            attribution='&copy; Esri'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Topo">
          <TileLayer
            attribution='&copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
            url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="USGS Topo">
          <TileLayer
            attribution='&copy; USGS'
            url="https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}"
          />
        </LayersControl.BaseLayer>
      </LayersControl>
      <IncidentZoneLayer />
      <FitBounds assessments={geoAssessments} />
      {geoAssessments.map(a => (
        <Marker
          key={a.id}
          position={[a.latitude!, a.longitude!]}
          icon={createColoredIcon(getColor(a))}
          eventHandlers={{ click: () => onMarkerClick?.(a) }}
        >
          <Popup>
            <Stack gap={4} style={{ minWidth: 220 }}>
              <Text fw={600} size="sm">{a.address || 'Unknown Location'}</Text>
              {a.county && <Text size="xs" c="dimmed">{a.county} County</Text>}
              <Group gap={6}>
                {a.damageCategory && (
                  <Badge size="xs" color={a.damageCategory === 'destroyed' ? 'red' : a.damageCategory === 'major' ? 'orange' : a.damageCategory === 'minor' ? 'yellow' : 'green'} tt="capitalize">
                    {a.damageCategory}
                  </Badge>
                )}
                {a.severity && <Badge size="xs" variant="outline" color={SEVERITY_COLORS[a.severity] ? undefined : 'gray'}>Severity {a.severity}</Badge>}
                <Badge size="xs" variant="light" tt="capitalize">{a.assessmentType}</Badge>
              </Group>
              {a.damageTypes && a.damageTypes.length > 0 && (
                <Group gap={4}>
                  {a.damageTypes.slice(0, 4).map(dt => (
                    <Badge key={dt} size="xs" variant="dot" tt="capitalize">{dt.replace(/_/g, ' ')}</Badge>
                  ))}
                  {a.damageTypes.length > 4 && <Text size="xs" c="dimmed">+{a.damageTypes.length - 4} more</Text>}
                </Group>
              )}
              {(a.homeCount ?? 0) > 1 && <Text size="xs" c="dimmed">{a.homeCount} homes/units</Text>}
              <Divider my={2} />
              <Button
                size="compact-xs"
                variant="light"
                leftSection={<IconExternalLink size={12} />}
                onClick={(e) => { e.stopPropagation(); navigate(`/impact/assessment/${a.id}`); }}
                fullWidth
              >
                View Full Assessment
              </Button>
            </Stack>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}
